let ctx = null;
let muted = localStorage.getItem("vts_arcade_muted") === "1";

const ac = () => {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

export const setArcadeMuted = (m) => { muted = m; localStorage.setItem("vts_arcade_muted", m ? "1" : "0"); };
export const isArcadeMuted = () => muted;

const tone = (freq, t0, dur, type = "triangle", gain = 0.12) => {
  const c = ac();
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, c.currentTime + t0);
  g.gain.setValueAtTime(0.0001, c.currentTime + t0);
  g.gain.exponentialRampToValueAtTime(gain, c.currentTime + t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + t0 + dur);
  o.connect(g);
  g.connect(c.destination);
  o.start(c.currentTime + t0);
  o.stop(c.currentTime + t0 + dur + 0.05);
};

const noise = (t0, dur, gain, freq) => {
  const c = ac();
  const len = Math.ceil(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i += 1) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = "bandpass";
  f.frequency.value = freq;
  f.Q.value = 0.7;
  const g = c.createGain();
  g.gain.setValueAtTime(gain, c.currentTime + t0);
  src.connect(f);
  f.connect(g);
  g.connect(c.destination);
  src.start(c.currentTime + t0);
};

export const sfxKick = () => { if (muted) return; try { noise(0, 0.12, 0.3, 280); } catch { /* audio blocked */ } };

export const sfxGoal = (intensity = 1) => {
  if (muted) return;
  try {
    const k = Math.min(Math.max(intensity, 1), 2.2);
    // 📣 louder crowd (user request) — roar + chant pulses boosted ~60%
    noise(0, 1.6 * k, 0.2 * k, 900);
    noise(0.1, 1.9 * k, 0.17 * k, 480);
    [523, 659, 784, 1047].forEach((f, i) => tone(f, 0.05 + i * 0.09, 0.5, "triangle", 0.16 * k));
    if (k >= 1.2) {
      // rhythmic crowd chant pulses for hot streaks
      [0.5, 0.85, 1.2, 1.55].forEach((t) => noise(t, 0.22, 0.2 * k, 650));
    }
  } catch { /* audio blocked */ }
};

// 🎙 commentator-style "GOOOAL!" voice sting (speech synthesis — no assets)
export const sfxCommentator = () => {
  if (muted) return;
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance("GOOOOOOAL!");
    u.rate = 0.85;
    u.pitch = 1.25;
    u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch { /* speech blocked */ }
};

export const sfxLaunch = () => {
  if (muted) return;
  try {
    const c = ac();
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(110, c.currentTime);
    o.frequency.exponentialRampToValueAtTime(760, c.currentTime + 0.7);
    g.gain.setValueAtTime(0.0001, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.09, c.currentTime + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.8);
    o.connect(g);
    g.connect(c.destination);
    o.start();
    o.stop(c.currentTime + 0.85);
    noise(0, 0.8, 0.16, 1400);
  } catch { /* audio blocked */ }
};

export const sfxBoom = () => {
  if (muted) return;
  try {
    const c = ac();
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(130, c.currentTime);
    o.frequency.exponentialRampToValueAtTime(36, c.currentTime + 0.6);
    g.gain.setValueAtTime(0.22, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.7);
    o.connect(g);
    g.connect(c.destination);
    o.start();
    o.stop(c.currentTime + 0.75);
    noise(0, 0.45, 0.28, 160);
  } catch { /* audio blocked */ }
};

export const sfxWin = () => {
  if (muted) return;
  try { [659, 880, 1319].forEach((f, i) => tone(f, i * 0.08, 0.35, "triangle", 0.1)); } catch { /* audio blocked */ }
};

export const sfxLoss = () => {
  if (muted) return;
  try { tone(140, 0, 0.35, "sawtooth", 0.07); noise(0, 0.25, 0.18, 120); } catch { /* audio blocked */ }
};

export const sfxJackpot = () => {
  if (muted) return;
  try {
    [523, 659, 784, 1047, 1319, 1568].forEach((f, i) => tone(f, i * 0.12, 0.6, "triangle", 0.12));
    noise(0, 2.2, 0.1, 800);
    noise(0.3, 2.0, 0.08, 1200);
  } catch { /* audio blocked */ }
};

/* 🏎 Car Race */
export const sfxCountdownBeep = (final = false) => { if (muted) return; try { tone(final ? 880 : 440, 0, 0.15, "square", 0.1); } catch { /* audio blocked */ } };
export const sfxEngineRev = () => { if (muted) return; try { noise(0, 0.5, 0.25, 120); noise(0.15, 0.6, 0.2, 90); tone(70, 0, 0.7, "sawtooth", 0.08); } catch { /* audio blocked */ } };
export const sfxEngineRoar = () => { if (muted) return; try { tone(90, 0, 1.2, "sawtooth", 0.07); tone(55, 0, 1.2, "sawtooth", 0.06); noise(0, 1.1, 0.12, 160); } catch { /* audio blocked */ } };
export const sfxScreech = () => { if (muted) return; try { noise(0, 0.4, 0.22, 2200); } catch { /* audio blocked */ } };
export const sfxFinishHorn = () => { if (muted) return; try { tone(392, 0, 0.5, "square", 0.12); tone(494, 0.08, 0.5, "square", 0.1); noise(0.2, 0.8, 0.15, 900); } catch { /* audio blocked */ } };

/* 🏀 Basketball Shot */
export const sfxBounce = () => { if (muted) return; try { noise(0, 0.08, 0.3, 180); tone(110, 0, 0.1, "sine", 0.1); } catch { /* audio blocked */ } };
export const sfxSwish = () => { if (muted) return; try { noise(0, 0.3, 0.2, 2800); } catch { /* audio blocked */ } };
export const sfxClang = () => { if (muted) return; try { tone(620, 0, 0.25, "square", 0.12); tone(615, 0.02, 0.3, "triangle", 0.1); noise(0, 0.12, 0.15, 1200); } catch { /* audio blocked */ } };
